/** /play/:code — the phone experience for players and viewers. */
import { formatNumber } from "@shared/i18n";
import { REACTION_EMOJI, type ServerEvent } from "@shared/protocol";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import {
  ArtifactCard,
  Leaderboard,
  PhoneCall,
  Radar,
  ReactionsLayer,
  RoleBadge,
  TimerRing,
  VoteBars,
} from "../components/game";
import { A11yButton, Brand, ConnectionBanner, Page } from "../components/Header";
import { hasIcon, Icon } from "../components/Icon";
import { Button, Card, Pill, QualityPill, Spinner, toast } from "../components/ui";
import { useI18n } from "../i18n";
import { useRoom, useServerClock } from "../lib/room";
import {
  type PlayerScenario,
  type RevealCall,
  type RevealDecision,
  useReveal,
  useScenario,
} from "../lib/scenario";
import { clearJoin, loadJoin } from "../lib/session-store";
import { navigate } from "../router";

export function Play({ code }: { code: string }) {
  const { t, locale } = useI18n();
  const saved = useMemo(() => loadJoin(code), [code]);
  useEffect(() => {
    if (!saved) navigate(`/j/${code}`, true);
  }, [saved, code]);
  const room = useRoom(saved?.wsPath ?? null);
  const { state, me, status } = room;
  const { data } = useScenario<PlayerScenario>(state?.scenarioId ?? null, locale, "player", code);
  const scenario = data?.scenario ?? null;

  const [reactionSubs] = useState(() => new Set<(e: string) => void>());
  const subscribeReactions = useCallback(
    (fn: (e: string) => void) => {
      reactionSubs.add(fn);
      return () => {
        reactionSubs.delete(fn);
      };
    },
    [reactionSubs],
  );

  useEffect(() => {
    return room.onEvent((e: ServerEvent) => {
      switch (e.type) {
        case "reaction":
          for (const fn of reactionSubs) fn(e.reaction.emoji);
          break;
        case "toast":
          toast(t(e.key, e.params), e.level);
          break;
        case "revealed":
          if ("vibrate" in navigator) navigator.vibrate?.(e.quality === "best" ? [40, 40, 40] : 80);
          break;
        case "kicked":
          clearJoin(code);
          break;
        default:
          break;
      }
    });
  }, [room, reactionSubs, t, code]);

  // Keep the phone awake during a live session when the browser allows it.
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: "screen"): Promise<{ release: () => Promise<void> }> };
    };
    const request = async () => {
      try {
        if (nav.wakeLock && document.visibilityState === "visible")
          lock = await nav.wakeLock.request("screen");
      } catch {
        /* not permitted */
      }
    };
    if (state?.status === "live") request();
    const onVis = () => {
      if (document.visibilityState === "visible" && state?.status === "live") request();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      lock?.release().catch(() => undefined);
    };
  }, [state?.status]);

  if (!saved) return null;

  if (status === "kicked") {
    return (
      <Shell>
        <Card class="stack center">
          <Icon name="phone-off" class="lg" style={{ margin: "0 auto", color: "var(--bad)" }} />
          <h1>{t("player.kicked.title")}</h1>
          <p>{t("player.kicked.body")}</p>
          <Button onClick={() => navigate("/join")}>{t("nav.join")}</Button>
        </Card>
      </Shell>
    );
  }

  if (!state || !me) {
    return (
      <Shell>
        <ConnectionBanner status={status} />
        <Spinner label={t("status.connecting")} />
      </Shell>
    );
  }

  const isViewer = me.kind === "viewer";
  const role = scenario?.roles.find((r) => r.id === me.roleId) ?? null;

  return (
    <Shell
      right={
        <>
          {!isViewer ? (
            <Pill tone="accent">
              <Icon name="sparkles" /> {formatNumber(locale, me.score)}
            </Pill>
          ) : (
            <Pill>
              <Icon name="eye" /> {t("join.asViewer")}
            </Pill>
          )}
          <A11yButton />
        </>
      }
    >
      <ConnectionBanner status={status} />
      {state.settings.reactions ? <ReactionsLayer subscribe={subscribeReactions} /> : null}
      {!scenario ? <Spinner /> : null}
      {scenario && state.status === "lobby" ? (
        <Lobby state={state} me={me} scenario={scenario} room={room} />
      ) : null}
      {scenario && state.status === "live" ? (
        <Live state={state} me={me} scenario={scenario} room={room} saved={saved} />
      ) : null}
      {scenario && (state.status === "debrief" || state.status === "ended") ? (
        <Debrief state={state} me={me} scenario={scenario} ended={state.status === "ended"} />
      ) : null}
      {scenario && !isViewer && state.settings.reactions && state.status !== "ended" ? (
        <ReactionBar onReact={(emoji) => room.send({ type: "react", emoji })} />
      ) : null}
      {role && state.status !== "lobby" ? (
        <details class="disclosure card tight" style={{ marginTop: "var(--s-4)" }}>
          <summary>
            <RoleBadge role={role} /> <span class="muted small">{t("player.brief")}</span>
          </summary>
          <p class="small">{role.brief}</p>
          {role.objectives.length ? (
            <ul class="small">
              {role.objectives.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>
          ) : null}
        </details>
      ) : null}
    </Shell>
  );
}

function Shell({
  children,
  right,
}: {
  children: preact.ComponentChildren;
  right?: preact.ComponentChildren;
}) {
  return (
    <Page>
      <header class="site-header">
        <Brand />
        <div class="row">{right}</div>
      </header>
      <main id="main" class="stack player-main">
        {children}
      </main>
    </Page>
  );
}

type RoomHandle = ReturnType<typeof useRoom>;
type S = NonNullable<RoomHandle["state"]>;
type Me = NonNullable<RoomHandle["me"]>;

function Lobby({
  state,
  me,
  scenario,
  room,
}: {
  state: S;
  me: Me;
  scenario: PlayerScenario;
  room: RoomHandle;
}) {
  const { t } = useI18n();
  const role = scenario.roles.find((r) => r.id === me.roleId) ?? null;
  const [picking, setPicking] = useState(false);
  const isViewer = me.kind === "viewer";
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of state.players) if (p.roleId) m.set(p.roleId, (m.get(p.roleId) ?? 0) + 1);
    return m;
  }, [state.players]);
  return (
    <>
      <Card class="stack">
        <span class="eyebrow">{t("status.lobby")}</span>
        <h1 style={{ margin: 0 }}>{t("player.lobby.title")}</h1>
        <p class="fg-2">{t("player.lobby.waiting")}</p>
        {!isViewer ? (
          <div class="row between">
            <div class="row">
              <span style={{ fontSize: "1.6rem" }} aria-hidden="true">
                {state.players.find((p) => p.id === me.playerId)?.avatar}
              </span>
              <strong>
                <bdi>{me.name}</bdi>
              </strong>
              <RoleBadge role={role} />
            </div>
            {state.settings.openRoleSelection ? (
              <Button size="sm" variant="secondary" onClick={() => setPicking((v) => !v)}>
                {t("player.lobby.changeRole")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {picking ? (
          <div class="choice-list" role="radiogroup" aria-label={t("join.roleLabel")}>
            <button
              type="button"
              role="radio"
              class="choice"
              aria-checked={me.roleId === null}
              onClick={() => {
                room.send({ type: "chooseRole", roleId: null });
                setPicking(false);
              }}
            >
              <Icon name="users" />
              <div class="title">{t("join.roleAny")}</div>
            </button>
            {scenario.roles.map((r) => {
              const taken = counts.get(r.id) ?? 0;
              const full = r.maxPlayers !== undefined && taken >= r.maxPlayers && me.roleId !== r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="radio"
                  class="choice"
                  aria-checked={me.roleId === r.id}
                  disabled={full}
                  onClick={() => {
                    room.send({ type: "chooseRole", roleId: r.id });
                    setPicking(false);
                  }}
                >
                  <Icon name={hasIcon(r.icon) ? r.icon : "user"} />
                  <div class="grow">
                    <div class="title">{r.name}</div>
                    <div class="sub">
                      {r.maxPlayers
                        ? t("join.roleTaken", { taken, max: r.maxPlayers })
                        : t("join.roleCount", { count: taken })}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </Card>
      {role ? (
        <Card class="stack-sm">
          <h2 style={{ fontSize: "var(--text-lg)" }}>{t("player.brief")}</h2>
          <p>{role.brief}</p>
          {role.objectives.length ? (
            <>
              <strong class="small">{t("player.objectives")}</strong>
              <ul>
                {role.objectives.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
            </>
          ) : null}
        </Card>
      ) : null}
      <Card class="stack-sm">
        <h2 style={{ fontSize: "var(--text-lg)" }}>{scenario.title}</h2>
        <p class="fg-2">{scenario.summary}</p>
        <p class="muted small">{t("join.playersIn", { count: state.playerCount })}</p>
      </Card>
    </>
  );
}

function Live({
  state,
  me,
  scenario,
  room,
  saved,
}: {
  state: S;
  me: Me;
  scenario: PlayerScenario;
  room: RoomHandle;
  saved: NonNullable<ReturnType<typeof loadJoin>>;
}) {
  const { t, locale } = useI18n();
  const nodeId = state.nodeId!;
  const node = scenario.nodes[nodeId];
  const isViewer = me.kind === "viewer";
  const voteOpen = Boolean(state.vote && !state.vote.result && state.vote.nodeId === nodeId);
  const now = useServerClock(room, voteOpen || Boolean(state.poll && !state.poll.closed));
  const revealed = Boolean(state.vote?.result && state.vote.nodeId === nodeId);
  const callFullyShown = node?.type === "call" && state.revealedLines >= node.transcript.length;
  const reveal = useReveal(state.code, nodeId, saved.token, locale, revealed || callFullyShown);

  if (!node) return <Spinner />;

  if (state.paused) {
    return (
      <Card class="signal center stack-sm">
        <Icon name="pause" class="lg" style={{ margin: "0 auto" }} />
        <strong>{t("status.paused")}</strong>
      </Card>
    );
  }

  switch (node.type) {
    case "narrative":
      return (
        <Card class="stack fade-in" key={nodeId}>
          <span class="eyebrow">{node.title}</span>
          <p style={{ whiteSpace: "pre-line" }}>{node.body}</p>
          {node.artifact ? <ArtifactCard artifact={node.artifact} /> : null}
          <RoleNote me={me} />
        </Card>
      );
    case "reveal":
      return (
        <Card class="stack fade-in" key={nodeId}>
          <span class="eyebrow">{t("player.reveal.title")}</span>
          <h2>{node.title}</h2>
          <p style={{ whiteSpace: "pre-line" }}>{node.body}</p>
          {node.lessons.length ? (
            <ul>
              {node.lessons.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          ) : null}
        </Card>
      );
    case "call":
      return (
        <div class="stack" key={nodeId}>
          <PhoneCall
            node={node}
            revealedLines={state.revealedLines}
            reveal={reveal?.type === "call" ? (reveal as RevealCall) : null}
            compact
          />
          <RoleNote me={me} />
        </div>
      );
    case "decision": {
      const vote = state.vote?.nodeId === nodeId ? state.vote : null;
      const eligible = node.voters === "all" || (me.roleId !== null && node.voters.includes(me.roleId));
      const decisionReveal = reveal?.type === "decision" ? (reveal as RevealDecision) : null;
      const optionsForBars = node.options.map((o) => ({
        id: o.id,
        label: o.label,
        quality: decisionReveal?.options.find((x) => x.id === o.id)?.quality,
      }));
      return (
        <div class="stack" key={nodeId}>
          <Card class="stack">
            <div class="row between">
              <span class="eyebrow">{node.title}</span>
              {vote && !vote.result ? (
                <TimerRing closesAt={vote.closesAt} durationSec={vote.durationSec} now={now} size={72} />
              ) : null}
            </div>
            <h2 style={{ fontSize: "var(--text-xl)" }}>{node.prompt}</h2>
            {node.context ? <p class="fg-2 small">{node.context}</p> : null}
            <RoleNote me={me} />
          </Card>

          {vote?.result ? (
            <Card class="stack fade-in">
              <span class="eyebrow">{t("player.result.roomChose")}</span>
              {(() => {
                const winner = node.options.find((o) => o.id === vote.result!.optionId);
                const q = decisionReveal?.options.find((o) => o.id === vote.result!.optionId);
                return (
                  <>
                    <h3 style={{ margin: 0 }}>{winner?.label}</h3>
                    {q ? <QualityPill quality={q.quality} /> : null}
                    {q ? <p>{q.feedback}</p> : null}
                  </>
                );
              })()}
              {!isViewer ? (
                me.lastPoints && me.myVote ? (
                  <div class="card sunken tight stack-sm">
                    <div class="row between">
                      <strong>{t("player.result.points", { points: me.lastPoints.points })}</strong>
                      <QualityPill quality={me.lastPoints.quality} />
                    </div>
                    <div class="row muted small">
                      {Object.entries(me.lastPoints.breakdown)
                        .filter(([, v]) => v > 0)
                        .map(([k, v]) => (
                          <span key={k}>
                            {t(`player.result.breakdown.${k}`)} +{v}
                          </span>
                        ))}
                    </div>
                  </div>
                ) : (
                  <p class="muted small">{t("player.result.noVote")}</p>
                )
              ) : null}
              <VoteBars
                options={optionsForBars}
                tally={vote.tally}
                winnerId={vote.result.optionId}
                showQuality
                mine={me.myVote}
              />
              {decisionReveal ? (
                <details class="disclosure">
                  <summary>{t("screen.results")}</summary>
                  <ul>
                    {decisionReveal.options
                      .filter((o) => o.id !== vote.result!.optionId)
                      .map((o) => (
                        <li key={o.id}>
                          <strong>{o.label}</strong> — <QualityPill quality={o.quality} /> {o.feedback}
                        </li>
                      ))}
                  </ul>
                </details>
              ) : null}
            </Card>
          ) : isViewer ? (
            <Card class="center stack-sm">
              <Icon name="eye" class="lg" style={{ margin: "0 auto" }} />
              <p class="muted">{t("screen.vote.open")}</p>
              {vote ? (
                <p class="small">
                  {t("player.vote.progress", { cast: vote.votesCast, eligible: vote.eligible })}
                </p>
              ) : null}
            </Card>
          ) : !eligible ? (
            <Card class="center stack-sm">
              <p class="muted">
                {t("player.vote.notEligible", {
                  roles: (node.voters as string[])
                    .map((r) => scenario.roles.find((x) => x.id === r)?.name ?? r)
                    .join(", "),
                })}
              </p>
            </Card>
          ) : vote ? (
            <div class="stack-sm">
              <p class="muted small" id="vote-help">
                {t("player.vote.help")}
              </p>
              <div
                class="choice-list"
                role="radiogroup"
                aria-describedby="vote-help"
                aria-label={t("player.vote.title")}
              >
                {node.options.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    class="choice"
                    style={{ minHeight: 64 }}
                    aria-checked={me.myVote === o.id}
                    onClick={() => {
                      room.send({ type: "vote", nodeId, optionId: o.id });
                      if ("vibrate" in navigator) navigator.vibrate?.(15);
                    }}
                  >
                    <span
                      class="status-dot"
                      style={{
                        marginTop: 8,
                        background: me.myVote === o.id ? "var(--accent)" : "var(--line-strong)",
                      }}
                    />
                    <div>
                      <div class="title">{o.label}</div>
                      {o.detail ? <div class="sub">{o.detail}</div> : null}
                    </div>
                  </button>
                ))}
              </div>
              <p class="small center" role="status">
                {me.myVote ? (
                  <>
                    <Icon name="check" /> {t("player.vote.cast")} ·{" "}
                    {t("player.vote.progress", { cast: vote.votesCast, eligible: vote.eligible })}
                  </>
                ) : (
                  t("player.vote.progress", { cast: vote.votesCast, eligible: vote.eligible })
                )}
              </p>
            </div>
          ) : (
            <Spinner label={t("player.waitingScreen")} />
          )}
        </div>
      );
    }
    case "poll": {
      const poll = state.poll?.nodeId === nodeId ? state.poll : null;
      return (
        <div class="stack" key={nodeId}>
          <Card class="stack">
            <div class="row between">
              <span class="eyebrow">{t("player.poll.title")}</span>
              {poll && !poll.closed ? (
                <TimerRing closesAt={poll.closesAt} durationSec={node.timerSec} now={now} size={64} />
              ) : null}
            </div>
            <h2 style={{ fontSize: "var(--text-xl)" }}>{node.prompt}</h2>
            <p class="muted small">{t("player.poll.help")}</p>
          </Card>
          {poll?.closed || isViewer ? (
            <Card>
              <VoteBars options={node.options} tally={poll?.tally ?? null} mine={me.myPollVote} />
            </Card>
          ) : (
            <div class="choice-list" role="radiogroup" aria-label={t("player.poll.title")}>
              {node.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  class="choice"
                  aria-checked={me.myPollVote === o.id}
                  onClick={() => room.send({ type: "pollVote", nodeId, optionId: o.id })}
                >
                  <div class="title">{o.label}</div>
                </button>
              ))}
              {me.myPollVote ? (
                <p class="small center muted" role="status">
                  {t("player.poll.thanks")}
                </p>
              ) : null}
            </div>
          )}
        </div>
      );
    }
    case "debrief":
      return null;
    default:
      return <Spinner label={t("player.branchWait")} />;
  }
}

function RoleNote({ me }: { me: Me }) {
  const { t } = useI18n();
  if (!me.roleNote) return null;
  return (
    <aside class="card signal tight stack-sm" aria-label={t("player.roleNote")}>
      <span class="eyebrow" style={{ color: "var(--signal-ink)" }}>
        <Icon name="lock" /> {t("player.roleNote")}
      </span>
      <p style={{ margin: 0 }}>{me.roleNote}</p>
    </aside>
  );
}

function Debrief({
  state,
  me,
  scenario,
  ended,
}: {
  state: S;
  me: Me;
  scenario: PlayerScenario;
  ended: boolean;
}) {
  const { t, locale } = useI18n();
  const isViewer = me.kind === "viewer";
  const sorted = [...state.players].sort((a, b) => b.score - a.score);
  const rank = sorted.findIndex((p) => p.id === me.playerId) + 1;
  const badgeDefs = scenario.badges;
  return (
    <div class="stack">
      <Card class="stack">
        <span class="eyebrow">{ended ? t("player.ended.title") : t("player.debrief.title")}</span>
        {ended ? <p>{t("player.ended.body")}</p> : null}
        <div class="row between">
          <div>
            <div class="muted small">{t("player.debrief.team")}</div>
            <div class="mono" style={{ fontSize: "var(--text-3xl)", fontWeight: 700 }}>
              {state.readiness ?? "—"}
            </div>
          </div>
          {!isViewer ? (
            <div class="center">
              <div class="muted small">{t("player.debrief.yourScore")}</div>
              <div class="mono" style={{ fontSize: "var(--text-3xl)", fontWeight: 700 }}>
                {formatNumber(locale, me.score)}
              </div>
              {rank > 0 ? (
                <div class="small">{t("player.debrief.rank", { rank, total: state.playerCount })}</div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div style={{ display: "grid", placeItems: "center" }}>
          <Radar dimensions={scenario.dimensions} scores={state.dimensionScores} size={280} />
        </div>
      </Card>
      {!isViewer ? (
        <Card class="stack-sm">
          <h2 style={{ fontSize: "var(--text-lg)" }}>{t("player.debrief.badges")}</h2>
          {me.badges.length ? (
            <div class="row">
              {me.badges.map((b) => {
                const def = badgeDefs.find((x) => x.id === b);
                return (
                  <span key={b} class="pill accent" title={def?.description}>
                    <Icon name={def && hasIcon(def.icon) ? def.icon : "award"} /> {def?.name ?? b}
                  </span>
                );
              })}
            </div>
          ) : (
            <p class="muted small">{t("player.debrief.noBadges")}</p>
          )}
        </Card>
      ) : null}
      {state.settings.showLeaderboard ? (
        <Card class="stack-sm">
          <h2 style={{ fontSize: "var(--text-lg)" }}>{t("screen.leaderboard")}</h2>
          <Leaderboard players={state.players} roles={scenario.roles} meId={me.playerId} />
        </Card>
      ) : null}
    </div>
  );
}

function ReactionBar({ onReact }: { onReact: (emoji: string) => void }) {
  const { t } = useI18n();
  return (
    <section class="row reaction-bar" aria-label={t("player.react.label")}>
      {REACTION_EMOJI.map((e) => (
        <button
          key={e}
          type="button"
          class="btn ghost"
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: 0,
            fontSize: "1.4rem",
            borderRadius: "var(--r-full)",
          }}
          aria-label={e}
          onClick={() => onReact(e)}
        >
          {e}
        </button>
      ))}
    </section>
  );
}
